'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function EmzirmeDonemindeDolgu() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/emzirme-doneminde-dolgu-guvenli-mi-ertelenmeli-mi';
  const pageTitle = 'Emzirme Döneminde Dolgu: Güvenli mi, Ertelenmeli mi?';
  const pageDescription = 'Emzirme döneminde dolgu yaptırmak güvenli midir? Hyaluronik asit dolguların sütle geçiş olasılığı, olası riskler ve uzman önerileri.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Emzirme Döneminde Dolgu', href: '/tr/blog/emzirme-doneminde-dolgu-guvenli-mi-ertelenmeli-mi' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Emzirme döneminde dolgu uygulaması ve güvenlik"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-purple-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Emzirme & Güvenlik
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Emzirme Döneminde Dolgu: Güvenli mi, Ertelenmeli mi?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>18 Şubat 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Doğum sonrası dönemde kadın bedeni, hem fiziksel hem de duygusal olarak yeniden yapılanma sürecine girer.
                Bu dönemde yaşanan uyku düzensizliği, hormonal değişimler ve yorgunluk, yüz ifadesinde belirginleşen çökme, solgunluk veya hacim kaybı şeklinde hissedilir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu durum bazı anneleri dolgu uygulamaları gibi "hızlı toparlanma çözümleri"ne yöneltebilir.
                Ancak tıp dünyası net: emzirme döneminde dermal dolgu uygulamaları önerilmez.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Dolgu Maddeleri ve Etkileri
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Günümüzde en sık kullanılan dolgu maddeleri hyaluronik asit (HA) temellidir.
                HA, vücutta doğal olarak bulunan bir polisakkarittir; su tutma kapasitesi sayesinde ciltte dolgunluk ve nem sağlar.
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <p className="text-blue-800 mb-4 font-medium">
                  Dolgu enjeksiyonlarıyla:
                </p>
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>dudaklar şekillendirilir,</li>
                  <li>göz altı çukurları giderilir,</li>
                  <li>elmacık kemikleri belirginleştirilir.</li>
                </ul>
                <p className="text-blue-800 mt-4">
                  Ancak bu etkiler, gebelik ve emzirme dönemindeki hormonal değişimlerle öngörülemez hale gelir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Emzirme Döneminde Dolgu Güvenli mi?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu maddeleri cilt altına lokal olarak uygulanır ve sistemik dolaşıma çok az geçer.
                Ancak bu "teorik güvenlik" verisi, anne sütüyle geçişin imkânsız olduğu anlamına gelmez.
              </p>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
                <p className="text-yellow-800 font-medium">
                  FDA ve ISAPS (Uluslararası Estetik Cerrahi Derneği) verilerine göre:
                </p>
                <p className="text-yellow-800 italic">
                  "Emzirme döneminde dermal dolgu uygulamalarının güvenliğine dair yeterli insan verisi yoktur. Bu nedenle kozmetik amaçlı enjeksiyonlar ertelenmelidir."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Olası Riskler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Risk</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sütle geçiş riski (teorik)</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu maddelerinin mikro miktarda kana karışma olasılığı vardır.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Alerjik reaksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Emzirme döneminde bağışıklık değiştiği için risk artar.</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Asimetri ve ödem</td>
                      <td className="border border-gray-300 px-4 py-3">Hormon kaynaklı su tutulumu dolgunun dengesini bozar.</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Enfeksiyon</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt bariyeri zayıf olduğu için iyileşme gecikebilir.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  ⚠️ Bu riskler az görünse de, bilimsel güvence olmadan işlem yapılması önerilmez.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Uzman Görüşü
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Emzirme döneminde dolgu uygulaması bilimsel olarak yeterince araştırılmamıştır.
                  Her ne kadar hyaluronik asit doğal bir madde olsa da, bu dönemde vücut biyokimyası değiştiği için öngörülemeyen tepkiler görülebilir.
                  Annelik süreci tamamlanıp hormon dengesi oturduğunda sonuçlar çok daha başarılı olur."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Dolgu İçerikleri ve Anne Sütü
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-indigo-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Dolgu Türü</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">İçerik</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sütle Geçiş Potansiyeli</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hyaluronik Asit (HA)</td>
                      <td className="border border-gray-300 px-4 py-3">Doğal polisakkarit</td>
                      <td className="border border-gray-300 px-4 py-3">Düşük</td>
                      <td className="border border-gray-300 px-4 py-3">Klinik veri yok</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kalsiyum Hidroksiapatit (CaHA)</td>
                      <td className="border border-gray-300 px-4 py-3">Mineral bileşik</td>
                      <td className="border border-gray-300 px-4 py-3">Bilinmiyor</td>
                      <td className="border border-gray-300 px-4 py-3">Önerilmez</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Poli-L-Laktik Asit (PLLA)</td>
                      <td className="border border-gray-300 px-4 py-3">Sentetik biyomateryal</td>
                      <td className="border border-gray-300 px-4 py-3">Bilinmiyor</td>
                      <td className="border border-gray-300 px-4 py-3">Önerilmez</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Otolog yağ transferi</td>
                      <td className="border border-gray-300 px-4 py-3">Kişinin kendi yağı</td>
                      <td className="border border-gray-300 px-4 py-3">Düşük</td>
                      <td className="border border-gray-300 px-4 py-3">Doğum sonrası döneme ertelenmeli</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  Tüm ürünlerde ortak sorun, yeterli emzirme dönemi verisinin olmamasıdır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6. Emzirme Sonrası Dolgu Zamanlaması
              </h2>
              
              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <p className="text-green-800 mb-4 font-medium">
                  Dolgu uygulaması için en güvenli dönem:
                </p>
                <p className="text-green-800 text-lg font-semibold mb-4">
                  📅 Emzirmenin tamamen sona ermesinden 1–2 ay sonra.
                </p>
                <p className="text-green-800 mb-4">
                  Bu sürede:
                </p>
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Hormon seviyeleri normale döner,</li>
                  <li>Ciltte ödem azalır,</li>
                  <li>Dolgu uygulaması daha dengeli ve kalıcı olur.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7. Doğal Alternatifler (Emzirme Dostu)
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-teal-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Yöntem</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Güvenlik</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Topikal hyaluronik asit serumları</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt nemini artırır, geçici dolgunluk sağlar</td>
                      <td className="border border-gray-300 px-4 py-3">Güvenli</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">C vitamini & kolajen destekleri</td>
                      <td className="border border-gray-300 px-4 py-3">Cilt tonunu eşitler, parlaklık kazandırır</td>
                      <td className="border border-gray-300 px-4 py-3">Güvenli</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">PRP (Platelet Rich Plasma)</td>
                      <td className="border border-gray-300 px-4 py-3">Vücut kanından elde edilir, sütle etkileşimi yok</td>
                      <td className="border border-gray-300 px-4 py-3">Doktor onayıyla</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Yüz egzersizleri ve masaj</td>
                      <td className="border border-gray-300 px-4 py-3">Kas tonusunu korur</td>
                      <td className="border border-gray-300 px-4 py-3">Tamamen güvenli</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-teal-50 border-l-4 border-teal-400 p-4 mb-6">
                <p className="text-teal-800 font-medium">
                  Bu alternatiflerle hem cilt yenilenmesi desteklenir hem de emzirme güvenliği korunur.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8. Annelik, Estetik ve Duygusal Denge
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Doğum sonrası dönemde birçok anne, kendi bedenini eskisiyle kıyaslar.
                Oysa bu dönemde ciltteki değişikliklerin çoğu geçici ve hormon temellidir.
                Vücut, bebeği beslemek için enerji depolar; bu süreç tamamlandığında estetik denge de kendiliğinden geri döner.
              </p>

              <div className="bg-purple-50 border-l-4 border-purple-400 p-4 mb-6">
                <p className="text-purple-800 font-medium">
                  💬 Gerçek estetik, sabırla bekleyebilme gücüdür.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Emzirme döneminde dudak dolgusu yapılır mı?</h3>
                  <p className="text-gray-600">Hayır. Dudak bölgesi kanlanması yüksek olduğu için reaksiyon riski fazladır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Dolgu maddesi anne sütüne geçer mi?</h3>
                  <p className="text-gray-600">Kanıt yok, ancak olasılık nedeniyle önerilmez.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Emzirme bittikten ne kadar sonra dolgu yapılabilir?</h3>
                  <p className="text-gray-600">Genellikle 1–2 ay sonra güvenli kabul edilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Doğal bir çözüm arıyorsam ne yapmalıyım?</h3>
                  <p className="text-gray-600">Nemlendirici, hyaluronik asit serum ve vitamin destekleri yeterlidir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sonuç
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Emzirme döneminde dolgu yaptırmak tıbben güvenli kabul edilmez çünkü yeterli veri yoktur.
                Bu dönemde anne sağlığı, cilt sağlığının da temelidir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Dolgu uygulamaları için sabırla beklemek, hem cilt hem de hormon dengesi açısından en doğru karardır.
                ✨ Annelik döneminde en güzel dolgu, annenin iç huzurudur.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>FDA (2024) – "Dermal Fillers and Lactation Safety"</li>
                  <li>ISAPS Global Report (2024) – "Guidelines for Injectables in Breastfeeding Women"</li>
                  <li>Harvard Health Publishing (2024) – "Cosmetic Treatments During Lactation"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Emzirme Döneminde Estetik Müdahale Rehberi"</li>
                  <li>American Academy of Dermatology (AAD) – "Postpartum Skin Care and Aesthetic Procedures"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#dolgu</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#emzirme</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#annelik</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#güvenlik</span>
                <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-purple-600 to-indigo-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Emzirme Döneminde Güvenli Estetik Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan emzirme döneminde güvenli estetik seçenekleri hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Emzirme döneminde estetik seçenekleri hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-purple-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/hamilelikte-dolgu-bilimsel-veriler-riskler-ve-uzman-yaklasimi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Hamilelikte Dolgu</h4>
              <p className="text-gray-600 text-sm">Hamilelikte dolgu yaptırmak güvenli mi? Bilimsel veriler ve uzman önerileri.</p>
            </a>
            <a
              href="/tr/blog/hamilelikte-botoks-bilimsel-gercekler-riskler-ve-uzman-gorusu"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Hamilelikte Botoks</h4>
              <p className="text-gray-600 text-sm">Hamilelikte botoksun güvenliği, riskleri ve uzman önerileri hakkında detaylı bilgiler.</p>
            </a>
            <a
              href="/tr/blog/orijinal-dolgu-anlama-yontemleri-sahte-urunlerden-korunma"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Orijinal Dolguyu Anlama Yöntemleri</h4>
              <p className="text-gray-600 text-sm">Orijinal dolgu nasıl anlaşılır ve sahte ürünlerden nasıl korunulur?</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
